import { FC } from 'react';
import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { APP, ROUTES } from 'config';
import {
  AdminShopUsersFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
} from 'components/filters';
import { shopUsersFiltersSchema, useShopUsers } from 'store/shop-users';
import { useSearchParams } from 'utils/navigation';

export const AdminShopUsersList: FC = () => {
  const { t } = useTranslation();

  const { searchParams: filters, navigate } = useSearchParams(
    shopUsersFiltersSchema,
  );

  const { data: shopUsers, isLoading } = useShopUsers(filters);

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={shopUsers?.total} />

          <Stack direction="row" alignItems="center" gap={1}>
            <Filters
              value={filters}
              initialValues={shopUsersFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              keysToExtractFromTotalCalculation={['search']}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('entity_type.employee')}
                    value={value.employeeIds}
                    initialValue={initialValues.employeeIds}
                    onChange={(v) => setFilter('employeeIds', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <AdminShopUsersFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={shopUsers?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'fullName',
            header: t('common.full_name'),
            cell: ({ row: { original: shopUser } }) => (
              <Link
                label={shopUser.employee.fullName}
                to={ROUTES.admin.shopUsers.details.replace(':id', shopUser.id)}
              />
            ),
            meta: { sort: true },
          },
          {
            id: 'email',
            header: t('common.email'),
            cell: ({ row: { original: shopUser } }) => shopUser.employee.email,
          },
          {
            id: 'phoneNumber',
            header: t('form.mobile_number'),
            cell: ({ row: { original: shopUser } }) => shopUser.employee.phone,
          },
          {
            id: 'parentCompanyName',
            header: t('marketplaceOrders.client'),
            cell: ({ row: { original: shopUser } }) => (
              <Link
                label={shopUser.parentCompany.name}
                to={ROUTES.admin.clients.details.settings.replace(
                  ':id',
                  shopUser.parentCompany.id,
                )}
              />
            ),
            meta: { sort: true },
          },
          {
            id: 'companyName',
            header: t('employees.company'),
            cell: ({ row: { original: shopUser } }) => shopUser.company.name,
            meta: { sort: true },
          },
          {
            id: 'lastActive',
            header: t('recognition.giversListLastActivity'),
            cell: ({ row: { original: shopUser } }) =>
              shopUser.lastActive
                ? moment(shopUser.lastActive).format('l LT')
                : '',
            meta: { sort: true },
          },
          {
            id: 'paymentCount',
            header: t('menu.orders'),
            cell: ({ row: { original: shopUser } }) => shopUser.paymentCount,
            meta: { sort: true, align: 'right' },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          shopUsers && {
            count: shopUsers.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
